import type { LeFlightSavedItemData } from 'tripPlanner/api/types'
import { optionToJourney } from 'tripPlanner/components/Chat/chatFlightTypes'
import type { FlightLeg, FlightOption } from 'tripPlanner/components/Chat/chatFlightTypes'
import { getDateFromDateTime, parseFlightDurationParts } from '../../utils/flightCartPanelUtils'

function timePart(dateTime: string | undefined): string {
  if (!dateTime) return ''
  const afterT = dateTime.split('T')[1]
  return (afterT ?? dateTime).slice(0, 5)
}

function datePart(date: string | undefined, legacyDateTime: string | undefined): string {
  if (date) return date
  // Legacy format only: pull the date out of a combined ISO date-time. A time-only
  // string (no 'T') carries no date, so return '' rather than a garbage value.
  return legacyDateTime?.includes('T') ? getDateFromDateTime(legacyDateTime) : ''
}

function buildSavedFareFamily(saved: LeFlightSavedItemData): App.FareFamily {
  const cabin = saved.cabinClass ?? ''

  return {
    name: cabin,
    carrier: saved.airline ?? '',
    description: '',
    fareFeatures: [],
  }
}

function mapSavedLayovers(saved: LeFlightSavedItemData): FlightLeg['layovers'] {
  return (saved.layovers ?? []).map((layover) => {
    const { hours, minutes } = parseFlightDurationParts(layover.duration)
    return {
      airportCode: layover.airportCode ?? '',
      airportName: layover.airportName ?? layover.airportCode ?? '',
      durationHours: hours,
      durationMinutes: minutes,
    }
  })
}

/**
 * Builds a JourneyV2 straight from a saved flight leg so it can be converted to
 * a bookable cart item WITHOUT a live re-search. The trip-chat flight-cart
 * endpoint doesn't re-price — it trusts the journeyKey + searchId + route/price
 * we already persisted — and the saved data carries every field a payload needs.
 *
 * Reuses optionToJourney so the resulting journey (and the cart payload built
 * from it) is structurally identical to the live-search path. Display-only fields
 * the saved data doesn't keep (airport names, city) fall back to the airport code.
 */
export function buildJourneyFromSavedFlight(
  saved: LeFlightSavedItemData,
): App.JourneyV2 | undefined {
  if (
    !saved.departureAirport ||
    !saved.arrivalAirport ||
    !saved.journeyKey ||
    typeof saved.price !== 'number' ||
    !Number.isFinite(saved.price)
  ) {
    return undefined
  }

  const { hours, minutes } = parseFlightDurationParts(saved.duration)
  const currencyCode = saved.currencyCode ?? saved.fareCurrencyCode ?? ''
  const price = saved.price

  const option: FlightOption = {
    leg: {
      departureAirport: saved.departureAirport,
      departureAirportName: saved.departureAirport,
      departureCity: '',
      departureTime: timePart(saved.departureTime),
      departureDate: datePart(saved.departureDate, saved.departureTime),
      arrivalAirport: saved.arrivalAirport,
      arrivalAirportName: saved.arrivalAirport,
      arrivalCity: '',
      arrivalTime: timePart(saved.arrivalTime),
      arrivalDate: datePart(saved.arrivalDate, saved.arrivalTime),
      durationHours: hours,
      durationMinutes: minutes,
      layovers: mapSavedLayovers(saved),
      airline: saved.airline ?? '',
      airlineLogo: saved.carrierLogo ?? '',
      flightNumber: saved.flightNumber ?? '',
    },
    price,
    pricePerPerson: price,
    currencyCode,
    carrier: saved.airline ?? '',
    carrierLogo: saved.carrierLogo ?? '',
    cabin: saved.cabinClass ?? '',
    journeyKey: saved.journeyKey,
    savedItemData: {
      source: 'le_flight',
      searchId: saved.searchId,
      searchExpiry: saved.searchExpiry,
      searchParams: saved.searchParams,
      journeyKey: saved.journeyKey,
      journeyId: saved.journeyId,
      currencyCode,
    },
  }

  const journey = optionToJourney(option, saved.journeyId ?? saved.journeyKey, currencyCode)

  return {
    ...journey,
    fareFamily: buildSavedFareFamily(saved),
  }
}
